import type { TowerKind, UnitKind } from "@empire/core-sim";
import palette from "../ui/theme/palette.json";

/**
 * How battle effects look (playtest readiness, Sep 2026).
 *
 * Kept apart from BoardView so the numbers a designer would tune — sizes,
 * colours, arcs — sit in one small file rather than in the middle of the
 * renderer. Subtle body weight complements the authored walk frames; large
 * deformation would fight the artwork and make soldiers look rubbery.
 */

/** Units are drawn this many times their table radius. 5 px dots were invisible on a phone. */
export const UNIT_SCALE = 1.75;

/** Each silhouette carries its weight differently, without changing its speed. */
const WEIGHT: Record<UnitKind, { bob: number; roll: number }> = {
  militia: { bob: 0.65, roll: 0.012 },
  shieldbearer: { bob: 0.4, roll: 0.009 },
  rider: { bob: 1.15, roll: 0.014 },
  ram: { bob: 0, roll: 0 },
  priest: { bob: 0.35, roll: 0.008 },
  saboteur: { bob: 0.55, roll: 0.014 },
  scout: { bob: 0.75, roll: 0.016 },
};

export function bodyMotion(kind: UnitKind, phase: number, turn: number, moving: boolean) {
  if (!moving) return { bob: 0, tilt: 0, squash: 1 };
  const weight = WEIGHT[kind];
  return {
    bob: -Math.abs(Math.sin(phase)) * weight.bob,
    tilt: Math.sin(phase) * weight.roll + turn * (kind === "ram" ? 0 : 0.035),
    squash: 1,
  };
}

export interface ShotStyle {
  readonly form: "streak" | "ball" | "beam";
  readonly colour: number;
  /** Line width for a streak, radius for a ball. */
  readonly size: number;
  /** Streaks: how much of the flight the streak covers (0..1). */
  readonly length: number;
  /** Pixels of arc at the top of the flight. */
  readonly arc: number;
  /** Radius the impact ring grows to. */
  readonly burst: number;
  /** Optional halo, for fire. */
  readonly glow?: number;
}

const hex = (css: string): number => Number.parseInt(css.replace("#", ""), 16);

const STYLES: Record<TowerKind, ShotStyle> = {
  arrow: { form: "streak", colour: 0xf3e3b3, size: 2, length: 0.28, arc: 10, burst: 8 },
  ballista: { form: "streak", colour: 0xd6a15c, size: 3.5, length: 0.4, arc: 2, burst: 12 },
  cannon: { form: "ball", colour: 0x2b2b2b, size: 4.5, length: 0, arc: 26, burst: 16 },
  brazier: {
    form: "ball",
    colour: 0xff8a2a,
    size: 4,
    length: 0,
    arc: 14,
    burst: 14,
    glow: 0xffc15a,
  },
  frost: { form: "beam", colour: 0x8fd8ff, size: 2, length: 1, arc: 0, burst: 10 },
};

const FALLBACK: ShotStyle = {
  form: "ball",
  colour: hex(palette.grain),
  size: 3,
  length: 0,
  arc: 9,
  burst: 9,
};

export function shotStyle(kind: TowerKind | undefined): ShotStyle {
  return kind ? STYLES[kind] : FALLBACK;
}

/** Green while healthy, amber at half, red when nearly dead. Input in thousandths. */
export function hpColour(permille: number): number {
  if (permille > 500) return hex(palette.ok);
  if (permille > 250) return hex(palette.warn);
  return hex(palette.danger);
}
